import { useTranslation } from 'next-i18next'
import React, { useEffect, useMemo, useState } from 'react'
import {
  deriveCanStoreVehicle,
  deriveLocationTypes,
  SPACE_TYPE_MAP
} from 'src/components/SearchBar/SpaceTypeFilters'
import { SpaceType } from 'src/components/SearchBar/types'
import useCommon from 'src/hooks/useCommon'
import ga from 'src/utils/pixels/gtag'
import { DEFAULT_ZOOM } from '../../constants'
import { useFilterProvider } from '../../context/FilterProvider'
import { SizeFilter, SortBy } from '../../types/graphql'
import { IQueryParams } from '../../types/types'
import {
  MAX_PRICE,
  MIN_PRICE,
  numberWithCommas,
  valueLabelFormat
} from '../../utils'
import CustomFilterDropdown from './CustomFilterDropdown'
import CustomFilterMultiSelect from './CustomFilterMultiSelect'
import FiltersDrawerMenu from './FiltersDrawerMenu'
import QuickFilterButton from './QuickFilterButton'
import { SingleSlider } from './styled'

type IFilters = Pick<
  IQueryParams,
  'sizes' | 'minPrice' | 'maxPrice' | 'zoom' | 'sortBy'
>
type IDrawers = {
  drawerPrice: boolean
  modalPrice: boolean
  isActivePrice: boolean
  anchorEl: HTMLElement | undefined
}

function QuickFilters() {
  const { t } = useTranslation('common')
  const { filters, updateParams, setFiltersOpen } = useFilterProvider()
  const sortByOptions = t('SORT_BY', { returnObjects: true })

  const [values, setValues] = useState<IFilters>({
    sizes: filters.sizes,
    minPrice: filters.minPrice,
    maxPrice: filters.maxPrice,
    zoom: filters.zoom ?? DEFAULT_ZOOM,
    sortBy: filters.sortBy ?? SortBy.Recommended
  })
  const [state, setState] = useState<IDrawers>({
    drawerPrice: false,
    modalPrice: false,
    isActivePrice: !!filters.minPrice && !!filters.maxPrice,
    anchorEl: undefined
  })

  useEffect(() => {
    setValues({
      sizes: filters.sizes,
      minPrice: filters.minPrice,
      maxPrice: filters.maxPrice,
      zoom: filters.zoom ?? DEFAULT_ZOOM,
      sortBy: filters.sortBy ?? SortBy.Recommended
    })
    setState({
      drawerPrice: false,
      modalPrice: false,
      isActivePrice: !!filters.minPrice || !!filters.maxPrice,
      anchorEl: undefined
    })
  }, [filters])

  const { sizeMapValues } = useCommon()
  const sizeDescriptions = t('SIZE_FILTER_DESC', {
    returnObjects: true
  }) as Record<string, string>

  // Filter size options based on active space types
  const filteredSizeOptions = useMemo(() => {
    const spaceTypes = filters.spaceTypes
    if (
      !spaceTypes ||
      spaceTypes.length === 0 ||
      spaceTypes.includes(SpaceType.ALL)
    ) {
      return sizeMapValues
    }

    const relevantSizes = new Set<string>()
    spaceTypes.forEach((st) => {
      const config = SPACE_TYPE_MAP[st]
      config?.query?.sizes?.forEach((s) => relevantSizes.add(s))
    })

    if (relevantSizes.size === 0) return sizeMapValues

    return Object.fromEntries(
      Object.entries(sizeMapValues).filter(([key]) => relevantSizes.has(key))
    )
  }, [filters.spaceTypes, sizeMapValues])

  const isSizesFiltered =
    !!filters.spaceTypes &&
    filters.spaceTypes.length > 0 &&
    !filters.spaceTypes.includes(SpaceType.ALL) &&
    Object.keys(filteredSizeOptions).length < Object.keys(sizeMapValues).length

  const isSizeActive = !!filters.sizes && filters.sizes.length > 0
  const isSortActive = !!filters.sortBy && filters.sortBy !== SortBy.Recommended

  const handleSizeSelect = (sizeKey: string | string[]) => {
    const newSizes = Array.isArray(sizeKey)
      ? (sizeKey as SizeFilter[])
      : [sizeKey as SizeFilter]
    setValues({ ...values, sizes: newSizes })
    // Same fix as the Filters drawer: derive canStoreVehicle AND
    // locationTypes from the selected sizes so the backend filters by type
    // and not just by area range.
    updateParams({
      sizes: newSizes,
      canStoreVehicle: deriveCanStoreVehicle(newSizes),
      locationTypes: deriveLocationTypes(newSizes),
      page: undefined
    })
    ga.event('select_category', {
      filter: 'size',
      value: newSizes.map((size) => sizeMapValues[size]).join(', '),
      // QuickFilters only renders on desktop (LandingNav secondRow ≥ md:);
      // the mobile path is MobileSearch → the full drawer (surface
      // mobile_drawer / desktop_modal, tagged in Filters/index.tsx).
      surface: 'quick_filters'
    })
  }

  const handleSortSelect = (sortKey: string | string[]) => {
    const newSortBy = (Array.isArray(sortKey) ? sortKey[0] : sortKey) as SortBy
    setValues({ ...values, sortBy: newSortBy })
    updateParams({ sortBy: newSortBy, page: undefined })
    ga.event('select_category', {
      filter: 'sort',
      value: newSortBy,
      surface: 'quick_filters'
    })
  }

  return (
    <div className="flex w-full items-center gap-2 px-1 text-ink-700 md:px-4">
      <div className="quick-filters scrollbar-hide flex items-center gap-2 overflow-x-auto pr-10 md:pr-0">
        {/* Size Filter */}
        <CustomFilterMultiSelect
          id="quick-filter-size"
          value={values.sizes ?? []}
          onChange={handleSizeSelect}
          options={filteredSizeOptions}
          optionDescriptions={sizeDescriptions}
          placeholder={t('FILTER.SIZE')}
          icon="si-ruler-combined"
          isActive={isSizeActive}
          size="small"
          footerLink={{
            label: `${t('FILTER.SIZE_GUIDE')} - ${t('FILTER.SIZE_GUIDE_CTA')}`,
            href: '/guia-de-tamanos'
          }}
          onShowAll={
            isSizesFiltered
              ? {
                  label: t('FILTER.SHOW_ALL_SIZES'),
                  onClick: () =>
                    updateParams({ spaceTypes: undefined, page: undefined })
                }
              : undefined
          }
        />

        {/* Price Filter */}
        <QuickFilterButton
          leading={<i className="si si-badge-dollar" />}
          title={
            state.isActivePrice &&
            (values.minPrice != undefined || values.maxPrice != undefined)
              ? `${!values.minPrice || values.minPrice <= 0 ? '$0' : `$${numberWithCommas(values.minPrice)}`} - ${!values.maxPrice || values.maxPrice >= 100000 ? '$100k+' : `$${numberWithCommas(values.maxPrice)}`}`
              : t('FILTER.PRICE')
          }
          onClickDrawer={() =>
            setState({
              ...state,
              drawerPrice: !state.drawerPrice,
              isActivePrice: !state.isActivePrice
            })
          }
          onClickMenu={(event: React.MouseEvent<HTMLButtonElement>) =>
            setState({
              ...state,
              modalPrice: true,
              anchorEl: event.currentTarget,
              isActivePrice: true
            })
          }
          isActive={state.isActivePrice}
        />
        <FiltersDrawerMenu
          title={t('FILTER.RENT_BY_MONTH')}
          subtitle={
            <span>
              {`${t('FILTER.FROM_PRICE')} `}
              <span className="font-semibold text-ink-800">
                {!values.minPrice || values.minPrice <= 0
                  ? t('FILTER.NO_MIN')
                  : `$${numberWithCommas(values.minPrice)}`}
              </span>
            </span>
          }
          trailing={
            <span>
              {`${t('FILTER.UNTIL_PRICE')} `}
              <span className="font-semibold text-ink-800">
                {!values.maxPrice || values.maxPrice >= 100000
                  ? t('FILTER.NO_MAX')
                  : `$${numberWithCommas(values.maxPrice)}`}
              </span>
            </span>
          }
          openDrawer={state.drawerPrice}
          openModal={state.modalPrice}
          anchor="bottom"
          anchorEl={state.anchorEl}
          onApply={() => {
            updateParams({
              minPrice: values.minPrice,
              maxPrice: values.maxPrice,
              page: undefined
            })
            setState({ ...state, drawerPrice: false, modalPrice: false })
            ga.event('select_category', {
              filter: 'price',
              value: `${values.minPrice}-${values.maxPrice}`,
              surface: 'quick_filters'
            })
          }}
          onClose={() => {
            setValues({
              ...values,
              minPrice: filters.minPrice,
              maxPrice: filters.maxPrice
            })
            setState({
              ...state,
              drawerPrice: false,
              modalPrice: false,
              isActivePrice:
                filters.minPrice != undefined && filters.maxPrice != undefined
            })
          }}
          onClear={() => {
            setValues({ ...values, minPrice: undefined, maxPrice: undefined })
            setState({
              ...state,
              drawerPrice: false,
              modalPrice: false,
              isActivePrice: false
            })
            updateParams({
              minPrice: undefined,
              maxPrice: undefined,
              page: undefined
            })
          }}
        >
          <div className="w-[300px] px-4">
            <SingleSlider
              min={0}
              step={1}
              max={180}
              value={[
                values.minPrice === MIN_PRICE || !values.minPrice
                  ? MIN_PRICE
                  : Math.log10((values.minPrice || 1) + 1) * 30,
                Math.log10((values.maxPrice || MAX_PRICE) + 1) * 30
              ]}
              onChange={(event, value) => {
                event.stopPropagation()
                if (Array.isArray(value)) {
                  const newValue = value as number[]
                  const minPrice =
                    newValue[0] === 0 ? 0 : Math.pow(10, newValue[0] / 30) - 1
                  const maxPrice = Math.pow(10, newValue[1] / 30) - 1
                  setValues({
                    ...values,
                    minPrice: Math.round(minPrice),
                    maxPrice: Math.round(maxPrice)
                  })
                }
              }}
              valueLabelFormat={valueLabelFormat}
              getAriaValueText={valueLabelFormat}
            />
          </div>
        </FiltersDrawerMenu>

        {/* Sort Filter */}
        <CustomFilterDropdown
          id="quick-filter-sort"
          value={values.sortBy}
          onChange={handleSortSelect}
          options={sortByOptions}
          placeholder={t('SEARCH_MANAGER.SORT')}
          icon="si-sort-vertical"
          isActive={isSortActive}
          size="small"
        />
      </div>

      {/* All Filters button - desktop */}
      <div className="ml-auto hidden shrink-0 pl-2 md:block">
        <button
          type="button"
          className="flex items-center gap-1.5 rounded-full border border-ink-100/20 px-3.5 py-2 text-[13px] font-medium text-ink-700 transition-colors duration-200 hover:border-ink-100/40 hover:bg-cream-300 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-800"
          onClick={() => setFiltersOpen(true)}
        >
          <i className="si si-sliders text-xs" />
          {t('FILTER.FILTER')}
        </button>
      </div>
    </div>
  )
}

export default React.memo(QuickFilters)
